import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { ChevronDown, Plus } from '@app/components/various/Icons';
import { cn } from '@app/utils/cn';
import { isEmpty } from '@app/utils/isEmpty';
import { Lookbook } from '@typings';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateLookbookFormOption } from './CreateLookbookFormOption';
import { DeliveryWindowsForm } from './DeliveryWindowsForm';
import { PricelistFormOption } from './PricelistFormOption';

const OPTION_KEYS = {
  ACTIVE_DELIVERY_WINDOWS: 'activeDeliveryWindows',
  RRP: 'rrp',
} as const;

const OPTION_FIELDS = {
  [OPTION_KEYS.ACTIVE_DELIVERY_WINDOWS]: ['activeDeliveryWindows'],
  [OPTION_KEYS.RRP]: ['pricelists'],
} as const;

type OptionKey = keyof typeof OPTION_FIELDS;

interface Props {
  isDisabled?: boolean;
}

export const CreateLookbookFormOptions = ({ isDisabled = false }: Props) => {
  const { t } = useTranslation(['common']);
  const [activatedOptions, setActivatedOptions] = React.useState<string[]>([]);

  const { resetField, setFocus } = useFormContext();
  const selectedMarketId = useWatch<Lookbook.CreateLookbookForm, 'market'>({ name: 'market' });
  const hasMarket = !isEmpty(selectedMarketId);

  const availableOptions = React.useMemo(() => {
    return [
      {
        isDisabled: activatedOptions.includes(OPTION_KEYS.ACTIVE_DELIVERY_WINDOWS),
        key: OPTION_KEYS.ACTIVE_DELIVERY_WINDOWS,
        label: t('common:delivery_window_other'),
        onClick: () => {
          setActivatedOptions([...activatedOptions, OPTION_KEYS.ACTIVE_DELIVERY_WINDOWS]);
          requestAnimationFrame(() => setFocus('deliveryWindows'));
        },
      },
      {
        isDisabled: activatedOptions.includes(OPTION_KEYS.RRP),
        key: OPTION_KEYS.RRP,
        label: t('common:recommended_retail_price'),
        onClick: () => {
          setActivatedOptions([...activatedOptions, OPTION_KEYS.RRP]);
          requestAnimationFrame(() => setFocus('pricelists'));
        },
      },
    ];
  }, [activatedOptions, setFocus, t]);

  const areAllOptionsActivated = activatedOptions.length === availableOptions.length;
  const shouldDisableOptions = areAllOptionsActivated || !hasMarket;

  const handleRemoveOption = (optionKey: OptionKey) => {
    OPTION_FIELDS[optionKey].forEach(field => resetField(field));

    setActivatedOptions(activatedOptions.filter(option => option !== optionKey));
  };

  return (
    <>
      {activatedOptions.map(optionKey => (
        <React.Fragment key={optionKey}>
          {optionKey === OPTION_KEYS.ACTIVE_DELIVERY_WINDOWS && (
            <CreateLookbookFormOption
              label={t('common:delivery_window_other')}
              isDisabled={isDisabled}
              onRemove={() => handleRemoveOption(OPTION_KEYS.ACTIVE_DELIVERY_WINDOWS)}
            >
              <DeliveryWindowsForm />
            </CreateLookbookFormOption>
          )}
          {optionKey === OPTION_KEYS.RRP && (
            <CreateLookbookFormOption
              label={`${t('common:recommended_retail_price')} (RRP)`}
              isDisabled={isDisabled}
              onRemove={() => handleRemoveOption(OPTION_KEYS.RRP)}
            >
              <PricelistFormOption />
            </CreateLookbookFormOption>
          )}
        </React.Fragment>
      ))}
      <Dropdown title={t('common:add_criteria')} placement="bottom-start">
        <DropdownTrigger>
          <button
            type="button"
            disabled={shouldDisableOptions}
            className={cn('mt-6 flex w-fit items-center gap-2 rounded-regular', {
              'cursor-not-allowed text-neutral-60': shouldDisableOptions,
            })}
          >
            <span className="flex items-center gap-1 body-2">
              <Plus size={16} />
              {t('common:add_criteria')}
            </span>
            <ChevronDown size={12} />
          </button>
        </DropdownTrigger>
        <DropdownMenu items={availableOptions} />
      </Dropdown>
    </>
  );
};
